import CryptoJS from 'crypto-js';

/**
 * Encrypts the given text using AES encryption with a key and IV from environment variables.
 * @param {string} text - The plaintext data to encrypt.
 * @returns {string|boolean} - The encrypted string or false in case of an error.
 */
export const encryptData = (text) => {
    try {
        const key_hex = process.env.REACT_APP_KEY;
        const iv_hex = process.env.REACT_APP_IV;

        const key = CryptoJS.enc.Hex.parse(key_hex);
        const iv = CryptoJS.enc.Hex.parse(iv_hex);

        const encrypted = CryptoJS.AES.encrypt(text, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });
        return encrypted.toString();
    } catch (error) {
        console.error("encryptData Exception = ", error);
        return false;
    }
};

/**
 * Decrypts the given AES encrypted text using a key and IV from environment variables.
 * @param {string} encryptedText - The encrypted string to decrypt.
 * @returns {string} - The decrypted plaintext string.
 */
export const decryptData = (encryptedText) => {
    const key = CryptoJS.enc.Hex.parse(process.env.REACT_APP_KEY);
    const iv = CryptoJS.enc.Hex.parse(process.env.REACT_APP_IV);

    const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
};

/**
 * Saves user-related data such as verificationToken, guid, and coachId into localStorage after encrypting it.
 * @param {Object} data - An object containing verificationToken, guid, and optionally coachId.
 */
export const saveUserData = (data) => {
    if (data && data.verificationToken && data.guid) {
        const encryptedVerificationToken = encryptData(data.verificationToken);
        const encryptedGuid = encryptData(data.guid);
        localStorage.setItem('verificationToken', encryptedVerificationToken);
        localStorage.setItem('guid', encryptedGuid);

        // Optional: Save coachId if provided
        if (data.coachId) {
            const encryptedCoachId = encryptData(data.coachId);
            localStorage.setItem('coachId', encryptedCoachId);
        }
    } else {
        console.error('Invalid data object. Expected {verificationToken, guid, coachId}');
    }
};

/**
 * Retrieves and decrypts the verificationToken from localStorage.
 * @returns {string|null} - The decrypted verificationToken or null if not found.
 */
export const getVerificationToken = () => {
    const encryptedVerificationToken = localStorage.getItem('verificationToken');
    if (encryptedVerificationToken) {
        return decryptData(encryptedVerificationToken);
    } else {
        console.warn('Verification token not found in storage');
        return null;
    }
};

/**
 * Retrieves and decrypts the guid from localStorage.
 * @returns {string|null} - The decrypted guid or null if not found.
 */
export const getGuid = () => {
    const encryptedGuid = localStorage.getItem('guid');
    if (encryptedGuid) {
        return decryptData(encryptedGuid);
    } else {
        console.warn('GUID not found in storage');
        return null;
    }
};

/**
 * Retrieves and decrypts the coachId from localStorage.
 * @returns {string|null} - The decrypted coachId or null if not found.
 */
export const getCoachId = () => {
    const encryptedCoachId = localStorage.getItem('coachId');
    if (encryptedCoachId) {
        return decryptData(encryptedCoachId);
    } else {
        console.warn('CoachId not found in storage');
        return null;
    }
};
